import { useCallback, useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import styled from '@emotion/styled';
import { flip, useFloating } from '@floating-ui/react';
import { cursorPositioner } from '@remirror/extension-positioner';
import { useMentionAtom, usePositioner } from '@remirror/react';
import { FloatingWrapper } from '@trinity/chat-components/components/Remirror/FloatingWrapper';
import type { AtMentionItem } from './AtMentionContext';
import { useAtMentionContext } from './AtMentionContext';
import { useAtMentionSuggestions } from './useAtMentionSuggestions';

type MentionItem = AtMentionItem & {
  id: string;
  label: string;
};

export const AtMentionDropdown = () => {
  const [, , displayConfig] = useAtMentionContext();
  const [mentionItems, setMentionItems] = useState<MentionItem[]>([]);

  const { state, getMenuProps, getItemProps, indexIsSelected } = useMentionAtom<MentionItem>({
    items: mentionItems,
  });

  // state from useMentionAtom is non-null only when the user has typed '@' (which inherently means the editor is focused)
  const enabled = !!state;

  const query = state?.query?.full?.toLowerCase() ?? '';
  const { items: fetchedItems, isLoading, hasMore, loadMore } = useAtMentionSuggestions(query, enabled);

  useEffect(() => {
    const adapted = fetchedItems.map((item) => ({
      ...item,
      id: item.entityId,
      label: item.name,
    }));
    setMentionItems(adapted);
  }, [fetchedItems]);

  const position = usePositioner(cursorPositioner, []);
  const [open, setOpen] = useState<boolean>(false);

  const { refs, floatingStyles } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-start',
    middleware: [flip()],
    elements: {
      reference: position.element,
    },
  });

  const subtitleFieldKey = displayConfig?.subtitleFieldKey;

  const getSubtitle = useCallback(
    (item: MentionItem): string | undefined => {
      if (subtitleFieldKey === '') return undefined;
      if (subtitleFieldKey) {
        return (item as Record<string, unknown>)[subtitleFieldKey] as string | undefined;
      }
      return item.entityId;
    },
    [subtitleFieldKey]
  );

  const renderItem = useCallback(
    (item: MentionItem, index: number) => {
      const isSelected = indexIsSelected(index);
      const itemProps = getItemProps({ item, index });
      const subtitle = getSubtitle(item);

      return (
        <MenuItem key={item.entityId} isSelected={isSelected} {...itemProps}>
          <ItemName>{item.name}</ItemName>
          {subtitle !== undefined && <ItemSubtitle>{subtitle}</ItemSubtitle>}
        </MenuItem>
      );
    },
    [getItemProps, indexIsSelected, getSubtitle]
  );

  const menuProps = getMenuProps();

  return (
    <FloatingWrapper position={position}>
      <MenuContainer ref={refs.setFloating} style={floatingStyles}>
        <AtMentionMenuContent
          enabled={enabled}
          headerLabel={displayConfig?.headerLabel}
          menuProps={menuProps}
          mentionItems={mentionItems}
          isLoading={isLoading}
          hasMore={hasMore}
          renderItem={renderItem}
          loadMore={loadMore}
        />
      </MenuContainer>
    </FloatingWrapper>
  );
};

AtMentionDropdown.displayName = 'AtMentionDropdown';

function AtMentionMenuContent({
  enabled,
  headerLabel,
  menuProps,
  mentionItems,
  isLoading,
  hasMore,
  renderItem,
  loadMore,
}: {
  enabled: boolean;
  headerLabel?: string;
  menuProps: object;
  mentionItems: MentionItem[];
  isLoading: boolean;
  hasMore: boolean;
  renderItem: (item: MentionItem, index: number) => ReactNode;
  loadMore: () => void;
}) {
  const handleLoadMoreMouseDown = (e: MouseEvent<HTMLButtonElement>) => {
    // Prevent editor from losing focus when clicking Load More
    e.preventDefault();
    loadMore();
  };

  return (
    <>
      {enabled && headerLabel && <MenuHeader>{headerLabel}</MenuHeader>}
      <Menu {...menuProps}>
        {enabled && mentionItems.map((item, index) => renderItem(item, index))}
        {enabled && !mentionItems.length && !isLoading && <NoResults>No results</NoResults>}
        {enabled && isLoading && mentionItems.length === 0 && <NoResults>Loading...</NoResults>}
        {enabled && hasMore && !isLoading && (
          <LoadMore onMouseDown={handleLoadMoreMouseDown}>
            Load more
          </LoadMore>
        )}
      </Menu>
    </>
  );
}

const MenuContainer = styled('div')({
  zIndex: 1300,
  position: 'absolute',
});

const MenuHeader = styled('div')(({ theme }) => ({
  padding: '6px 12px 4px',
  fontFamily: theme.fonts.semiBold,
  fontSize: 11,
  color: theme.palette.gray.main,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  borderBottom: `1px solid ${theme.palette.gray.light2}`,
}));

const Menu = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  border: `1px solid ${theme.palette.gray.light2}`,
  borderRadius: 4,
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  maxHeight: 240,
  overflowY: 'auto',
  minWidth: 200,
}));

const MenuItem = styled('div')<{ isSelected: boolean }>(({ theme, isSelected }) => ({
  padding: '8px 12px',
  cursor: 'pointer',
  backgroundColor: isSelected ? theme.palette.primary.light1 : 'transparent',
  '&:hover': {
    backgroundColor: theme.palette.gray.light2,
  },
}));

const ItemName = styled('div')(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 14,
  color: theme.palette.black.light1,
}));

const ItemSubtitle = styled('div')(({ theme }) => ({
  fontFamily: theme.fonts.regular,
  fontSize: 12,
  color: theme.palette.gray.main,
  marginTop: 2,
}));

const NoResults = styled('div')(({ theme }) => ({
  padding: '8px 12px',
  fontFamily: theme.fonts.regular,
  fontSize: 14,
  color: theme.palette.gray.main,
}));

const LoadMore = styled('button')(({ theme }) => ({
  display: 'block',
  width: '100%',
  padding: '8px 12px',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontFamily: theme.fonts.regular,
  fontSize: 14,
  color: theme.palette.primary.main,
  textAlign: 'left' as const,
  '&:hover': {
    textDecoration: 'underline',
  },
}));
